import { Component, EventListener, getComponentByDomNode } from '@gondel/core';
import { GondelJqueryComponent } from '@gondel/plugin-jquery';
import { browserDeviceTests } from '@swisslife/shared-ui/src/utils/browser-detection/js/browser-detection';
import eventBus, { SLEvent } from '@swisslife/sl-event-bus/src/sl-event-bus';
import ajax from '@swisslife/shared-ui/src/utils/ajax/js/ajax';
import { Header } from '../../header/js/header';
import { Overlay } from '@swisslife/patterns-base/src/organisms/overlay/js/overlay';
import {
	IGHeaderHiddenListenerEvent,
	IGScrollListenerEvent,
	IGScrollToTargetListenerEvent,
	IGLoadYoutubeApiListenerEvent,
	IGOverlayListenerEvent,
	GLoadYoutubeCallbackFunction,
} from '@swisslife/shared-types/src/types/gondel-public-events';

interface IRegisterStickyListenerOptions {
	element: HTMLElement;
	onStickyChange: (isSticky: boolean) => void;
}

// add your states here, e.g. Hidden: 'state-o-page--hidden',
enum States {
	NoScroll = 'state-l-layout--no-scroll',
	HeaderHidden = 'state-o-page--header-hidden',
}

enum Selectors {
	Header = '.js-o-header',
	Overlay = '.js-o-page__overlay',
	Main = '.js-l-main',
}

// The @Component decorator will connect the class with `data-g-name="Page"` elements.
@Component('Page')
export class Page extends GondelJqueryComponent {
	private scrollTop: number;
	private youtubeApiLoadedCallbacks: GLoadYoutubeCallbackFunction[] = [];
	private youtubeApiLoading: boolean = false;
	private stickyPatternIsSticky: boolean = false;
	private $header: JQuery;
	private header: Header | undefined;
	private $overlay: JQuery;
	private overlay: Overlay;

	public start() {
		this.vhCheck();
		this.addOrientationChangeListener();
		this.initSlEventBus();

		this.$header = $(Selectors.Header);

		if (this.$header.length > 0) {
			this.header = getComponentByDomNode<Header>(this.$header);
		}

		this.$overlay = $(Selectors.Overlay);

		if (this.$overlay.length > 0) {
			this.overlay = getComponentByDomNode<Overlay>(this.$overlay);
		}
	}

	public stop() {
		if (browserDeviceTests.ios || browserDeviceTests.android) {
			window.removeEventListener('orientationchange', () => null);
		}
	}

	@EventListener('gScrollHandler')
	public _handleScroll(event: IGScrollListenerEvent) {
		event.data.eventData.scroll ? this.enableScroll() : this.disableScroll();
	}

	@EventListener('gVhCheckHandler')
	public _handleVhCheck() {
		this.vhCheck();
	}

	@EventListener('gHeaderHiddenHandler')
	public _handleHeaderHiddenStateChange(event: IGHeaderHiddenListenerEvent) {
		this.toggleStateClass(States.HeaderHidden, event.data.eventData.newState);
	}

	@EventListener('gScrollToTargetHandler')
	public _handleScrollToTarget(event: IGScrollToTargetListenerEvent) {
		this.scrollToTarget(event.data.eventData.target, event.data.eventData.duration, event.data.eventData.ctx);
	}

	@EventListener('gLoadYoutubeApiHandler')
	public _loadYoutubeApi(event: IGLoadYoutubeApiListenerEvent) {
		this.loadYoutubeApi(event.data.eventData.scriptUrl, event.data.eventData.fnCallback);
	}

	@EventListener('gOverlayHandler')
	public _handleShowOverlay(event: IGOverlayListenerEvent) {
		this.showOverlay(
			event.data.eventData.overlayComponent,
			event.data.eventData.overlayTitle,
			event.data.eventData.overlayContentUrl
		);
	}

	public enableScroll() {
		this.$ctx.removeClass(States.NoScroll);
		this.resetScrollPosition();
		this.unfreezeScrollPosition();
	}

	public disableScroll() {
		this.$ctx.addClass(States.NoScroll);
		this.saveScrollPosition();
		this.freezeScrollPosition();
	}

	private toggleStateClass(state: string, newState: boolean) {
		if (newState) {
			this.$ctx.addClass(state);
		} else {
			this.$ctx.removeClass(state);
		}
	}

	private scrollToTarget(target: string | JQuery<HTMLElement>, duration: number, ctx: string = 'html, body') {
		const $target = this.$ctx.find(target);
		const $ctx = $(ctx);

		if ($target.length === 0 || $ctx.length === 0) {
			return;
		}

		const targetOffset = $target.offset();

		if (!targetOffset) {
			return;
		}

		const ctxScrollTop = $ctx.scrollTop() || 0; // the scroll position of the ctx
		const targetOffsetTop = targetOffset.top; // the distance of the target relative to the document

		let headerOffset = 0;

		// if the scroll context is the page, we always subtract the header height
		// since we cannot always predict the scroll direction correctly
		if (this.header && ctx === 'html, body') {
			headerOffset = this.header.height;
		}

		$(ctx).animate(
			{
				scrollTop: targetOffsetTop - headerOffset,
			},
			duration
		);
	}

	private addOrientationChangeListener() {
		// Listen for orientation changes
		window.addEventListener(
			'orientationchange',
			() => {
				const newOffset = this.testVh();
				this.updateCssVar('vh-gap', newOffset);
			},
			false
		);
	}

	private vhCheck() {
		if (browserDeviceTests.ios || browserDeviceTests.android) {
			const offset = this.testVh();

			this.updateCssVar('vh-gap', offset);
		}
	}

	private saveScrollPosition() {
		if (browserDeviceTests.ios) {
			if (window.pageYOffset) {
				this.scrollTop = window.pageYOffset;
			}
		}
	}

	private freezeScrollPosition() {
		if (!browserDeviceTests.ios || this.scrollTop <= 0) {
			return;
		}
		$(Selectors.Main, this.$ctx).css('marginTop', this.scrollTop * -1);
	}
	private unfreezeScrollPosition() {
		if (!browserDeviceTests.ios) {
			return;
		}
		$(Selectors.Main, this.$ctx).removeAttr('style');
	}

	private resetScrollPosition() {
		if (browserDeviceTests.ios) {
			window.scrollTo(0, this.scrollTop);
			window.setTimeout(() => {
				this.scrollTop = 0;
			}, 0);
		}
	}

	private testVh() {
		// test with fixed
		const documentElement: any = document.documentElement;
		const fixedTest = document.createElement('div');
		fixedTest.style.cssText = 'position: fixed; top: 0; bottom: 0;';
		documentElement.insertBefore(fixedTest, documentElement.firstChild);

		// test with vh
		const vhTest = document.createElement('div');
		vhTest.style.cssText = 'position: fixed; top: 0; height: 100vh';
		documentElement.insertBefore(vhTest, documentElement.firstChild);

		// in iOS vh will be bigger
		const topBottom = fixedTest.offsetHeight;
		const vh = vhTest.offsetHeight;
		const difference = vh - topBottom;

		// clean
		documentElement.removeChild(fixedTest);
		documentElement.removeChild(vhTest);

		return difference;
	}

	private updateCssVar(cssVarName: string, offset: number) {
		const documentElement: any = document.documentElement;
		documentElement.style.setProperty(`--${cssVarName}`, `${offset}px`);
	}

	private async initSlEventBus() {
		const eb = await eventBus();
		eb.on(SLEvent.DisablePageScroll, () => this.disableScroll());
		eb.on(SLEvent.EnablePageScroll, () => this.enableScroll());

		if (this.$header.length > 0) {
			eb.on(
				SLEvent.RegisterStickyListener,
				(meta: string, { element, onStickyChange }: IRegisterStickyListenerOptions) => {
					this.initializeStickyListener($(element), onStickyChange);
				}
			);
			eb.on(SLEvent.UnregisterStickyListener, () => {
				$(document).off('scroll.sticky');
			});
		}
	}

	private initializeStickyListener($element: JQuery, onStickyChange: (isSticky: boolean) => void) {
		this.handleScrollSticky($element, onStickyChange);

		$(document).on('scroll.sticky', () => {
			this.handleScrollSticky($element, onStickyChange);
		});
	}

	private handleScrollSticky = ($element: JQuery, onStickyChange: (isSticky: boolean) => void) => {
		const itemTop = parseFloat(((($element.offset() || {}) as any).top || 0).toString());
		const top = parseFloat(($(document).scrollTop() || 0).toString());
		let headerHeight = 0;
		if (this.header) {
			headerHeight = this.header.isVisible ? this.header.height : 0;
		}
		// 8px more is needed because of the spacing between header and sticky-content
		const isSticky = top + headerHeight + 8 >= itemTop;

		if (isSticky === this.stickyPatternIsSticky) {
			return;
		}

		onStickyChange(isSticky);
		this.stickyPatternIsSticky = isSticky;
	};

	private loadYoutubeApi(scriptUrl: string, callbackFn: GLoadYoutubeCallbackFunction) {
		if (typeof (window as any).YT === 'undefined') {
			this.youtubeApiLoadedCallbacks.push(callbackFn);
			this.requestYoutubeAPi(scriptUrl);
		} else {
			callbackFn();
		}
	}

	private async requestYoutubeAPi(scriptUrl: string) {
		if (!this.youtubeApiLoading) {
			(window as any).onYouTubeIframeAPIReady = this.youtubeApiLoaded.bind(this);
			this.youtubeApiLoading = false;

			await ajax(scriptUrl, {
				dataType: 'script',
			});
		}
	}

	private youtubeApiLoaded() {
		$.each(this.youtubeApiLoadedCallbacks, (index, callbackFn) => {
			if (typeof callbackFn === 'function') {
				callbackFn();
			}
		});

		this.youtubeApiLoading = false;
	}

	private showOverlay(component: string, title: string, contentUrl: string) {
		if (title !== '' && contentUrl !== '') {
			this.overlay.setTitle(title);
			this.overlay.setFetchContentUrl(contentUrl);

			this.overlay.show({
				component,
			});
		}
	}
}
